import { accounts, CredentialResponse, PromptMomentNotification } from 'google-one-tap';
import { filter, Subject } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { Nullable, WINDOW } from '@pin-up-global/common';
import { GOOGLE_CLIENT_ID } from '@core/auth';

type WindowExtended = Window & { onGoogleLibraryLoad(): void };

declare namespace google {
  const accounts: accounts;
}

@Injectable()
export class GoogleAuthService {
  #window = inject<WindowExtended>(WINDOW);
  #googleClientId = inject<string>(GOOGLE_CLIENT_ID);
  #googleLibraryLoaded = new Promise<void>((resolve) => {
    this.#window.onGoogleLibraryLoad = () => resolve();
  });
  #googleCredential$ = new Subject<Nullable<string>>();

  googleCredential$ = this.#googleCredential$.pipe(
      filter(Boolean),
  );

  constructor() {
    this.#registerLoginCallback();
    this.#registerGoogleLibrary();
  }

  logout(): void {
    google.accounts.id.disableAutoSelect();
    this.#googleCredential$.next(null);
  }

  addSignInButton(parent: HTMLElement): void {
    this.#googleLibraryLoaded.then(() => {
      google.accounts.id.renderButton(parent, { width: 400 });
    });
  }

  addOneTapPrompt(callback?: (notification: PromptMomentNotification) => void): void {
    this.#googleLibraryLoaded.then(() => {
      google.accounts.id.prompt(callback);
    });
  }

  #registerGoogleLibrary(): void {
    const script = this.#window.document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;

    this.#window.document.body.appendChild(script);
  }

  #registerLoginCallback() {
    this.#googleLibraryLoaded.then(() => {
       google.accounts.id.initialize({
        // Ref: https://developers.google.com/identity/gsi/web/reference/js-reference#IdConfiguration
        client_id: this.#googleClientId,
        callback: (response: CredentialResponse): void => {
          if (response.credential) {
            this.#googleCredential$.next(response.credential);
          } else {
            this.logout();
          }
        },
      });
    });
  }
}
